import React, { useState } from "react";
import { ReactTyped } from "react-typed";
import { useNavigate } from "react-router-dom";
import SpeechRecognition, { useSpeechRecognition } from "react-speech-recognition";
import { motion } from "framer-motion";
import "./Home.css";

// Dummy project data
const projects = [
  { id: 1, title: "QR Scanner Pro", desc: "Flutter-based advanced QR Scanner app with history & custom features." },
  { id: 2, title: "E-Commerce React", desc: "A React JS based dark-themed e-commerce website with product filtering." },
];

const Home = () => {
  const navigate = useNavigate();
  const [error, setError] = useState("");

  const { transcript, listening, resetTranscript } = useSpeechRecognition();

  const handleVoiceCommand = () => {
    const cmd = transcript.toLowerCase();
    resetTranscript();
    if (cmd.includes("open project")) navigate("/projects");
    else if (cmd.includes("open skill")) navigate("/skills");
    else if (cmd.includes("open home")) navigate("/");
    else setError("❌ Page Not Found");
  };

  return (
    <div className="home-container">
      {/* Hero */}
      <motion.div className="hero" initial={{ opacity: 0, y: -30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
        <img
          src={require("../Me.png")}
          alt="Ritesh Avatar"
          className="hero-avatar"
          style={{
            width: "12rem",
            height: "12rem",
            borderRadius: "30%",
            border: "4px solid var(--accent)",
            boxShadow: "0 6px 20px var(--accent-25)",
            objectFit: "cover",
            transition: "transform 0.3s"
          }}
        />
        <h1>Hi, I’m Ritesh 👋</h1>
        <ReactTyped
          strings={[
            "React JS Developer ⚡",
            "Flutter Developer 🚀",
            "Data Science Enthusiast 📊",
            "Cyber Security Specialist 🛡️"
          ]}
          typeSpeed={50}
          backSpeed={30}
          loop
          className="typed-text"
        />
      </motion.div>

      {/* Featured Projects */}
      <div
        className="projects-section"
        style={{
          background: "var(--bg-dark, #181a1b)",
          color: "var(--text-dark, #f5f6fa)",
          borderRadius: "1.5rem",
          boxShadow: "0 4px 24px rgba(0,0,0,0.25)",
          padding: "2rem 1.5rem",
          marginBottom: "2.5rem",
          width: "100%",
          maxWidth: "900px"
        }}
      >
        <h2
          style={{
            color: "var(--accent, #00bfff)",
            fontWeight: 700,
            marginBottom: "1.5rem",
            letterSpacing: "0.02em"
          }}
        >
          Featured Projects
        </h2>
        <div
          className="project-list"
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "1.5rem",
            justifyContent: "center",
            marginBottom: "2rem"
          }}
        >
          {projects.map((p) => (
            <motion.div
              key={p.id}
              className="project-card"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              style={{
                background: "var(--card-dark, #23272f)",
                color: "var(--text-dark, #f5f6fa)",
                borderRadius: "1rem",
                boxShadow: "0 2px 12px rgba(0,0,0,0.18)",
                padding: "1.25rem 1rem",
                minWidth: "220px",
                maxWidth: "270px",
                flex: "1 1 220px",
                border: "1px solid var(--accent-25, #222b3a)"
              }}
            >
              <h3
                style={{
                  color: "var(--accent, #00bfff)",
                  fontSize: "1.15rem",
                  fontWeight: 600,
                  marginBottom: "0.5rem"
                }}
              >
                {p.title}
              </h3>
              <p
                style={{
                  color: "var(--text-dark, #f5f6fa)",
                  fontSize: "0.98rem",
                  opacity: 0.85
                }}
              >
                {p.desc}
              </p>
            </motion.div>
          ))}
        </div>
        <button
          className="cta-button"
          style={{
            background: "var(--accent, #00bfff)",
            color: "#fff",
            border: "none",
            borderRadius: "2rem",
            padding: "0.75rem 2.2rem",
            fontWeight: 700,
            fontSize: "1.1rem",
            boxShadow: "0 2px 8px rgba(0,0,0,0.13)",
            cursor: "pointer",
            transition: "background 0.2s"
          }}
          onClick={() => navigate("/projects")}
        >
          View More Projects
        </button>
      </div>

      {/* Education Timeline */}
      <div className="timeline">
        <h2>🎓 Education</h2>
        <div className="timeline-item">
          <p>📅 May 2022</p>
          <p className="title">H.S.C. in Commerce</p>
          <p className="text-sm">Started learning software development after finishing 12th.</p>
        </div>
        <div className="timeline-item">
          <p>📅 June 2022</p>
          <p className="title">B.Sc. (IT)</p>
          <p className="text-sm">Began journey in Information Technology studies.</p>
        </div>
        <div className="timeline-item">
          <p>📅 June 2025</p>
          <p className="title">Data Science Pro Program</p>
          <p className="text-sm">Excited to dive into the world of Data Science.</p>
        </div>
        <div className="timeline-item">
          <p>📅 Dec 2025</p>
          <p className="title">Certified in Data Science</p>
          <p className="text-sm">Completed program on data analysis & machine learning.</p>
        </div>
      </div>

      {/* AI Chatbot Intro */}
      <div className="ai-chat">
        <p>&gt; Who are you?</p>
        <p>Ritesh: A React & Flutter Developer 🚀</p>
        <p className="mt-2">&gt; What do you do?</p>
        <p>Ritesh: Build apps, analyze data, and solve problems.</p>
      </div>

      {/* Voice Command Buttons */}
      <div className="voice-controls">
        <button className="voice-btn-mic" onClick={() => listening ? SpeechRecognition.stopListening() : SpeechRecognition.startListening({continuous:true})}>
          {listening ? "🎙️ Listening..." : "🎤 Voice"}
        </button>
        <button className="voice-btn-execute" onClick={handleVoiceCommand}>Execute</button>
      </div>

      {/* Error Dialog */}
      {error && (
        <div className="error-dialog">
          {error}
          <button onClick={() => setError("")}>Close</button>
        </div>
      )}
    </div>
  );
};

export default Home;
